import React from 'react';
import { twMerge } from 'tailwind-merge';
import { SparkChart } from '@plerionhq/design-system/experimental';
import { Card, CardHeader, CardContent, CardFooter } from '../Card';
import { renderIcon } from '../Dashboard/iconUtils';
import { cn } from '../../../lib/utils';

import { useTranslation } from 'react-i18next';

export interface ProgressCardProps {
  /** The title of the progress card */
  title?: string;
  /** Current progress value (0-100) */
  progress?: number;
  /** Optional subtitle */
  subtitle?: string;
  /** Whether the card is loading */
  isLoading?: boolean;
  /** Optional custom className for styling */
  className?: string;
  /** Optional icon to display (Lucide icon name as string) */
  icon?: string;
  /** Optional click handler */
  onClick?: () => void;
  /** Color variant for the progress bar */
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  /** Whether to show the progress percentage */
  showPercentage?: boolean;
  /** Optional description text */
  description?: string;
  /** Optional target value */
  target?: number;
  /** Optional current value */
  current?: number;
  /** Optional unit for the values */
  unit?: string;
  /** Optional chart data points */
  chartData?: number[];
  /** Optional title icon */
  titleIcon?: string;
  /** Whether data is streaming from LLM */
  isStreaming?: boolean;
}

export const ProgressCard: React.FC<ProgressCardProps> = ({
  title,
  titleIcon,
  progress = 0,
  subtitle,
  isLoading = false,
  className,
  icon,
  onClick,
  variant = 'default',
  showPercentage = true,
  description,
  target,
  current,
  unit,
  chartData,
}) => {
  const { t } = useTranslation('pleri/dashboard');
  // Show loading state if explicitly loading or if no data is provided
  if (
    isLoading ||
    (!title && progress === undefined && !subtitle && !description)
  ) {
    return (
      <Card className={cn('h-full', className)} onClick={onClick}>
        <CardContent className="flex h-full items-center p-6">
          <div className="w-full animate-pulse">
            <div className="mb-2 h-4 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
            <div className="mb-2 h-2 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
            <div className="h-4 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  const getVariantStyles = () => {
    switch (variant) {
      case 'success':
        return {
          progress: 'bg-green-50 dark:bg-green-950',
          text: 'text-green-600',
          bg: 'bg-green-100 dark:bg-green-900',
          ring: 'ring-green-200/50',
          chartColor: '#218380',
        };
      case 'warning':
        return {
          progress: 'bg-yellow-50 dark:bg-yellow-950',
          text: 'text-yellow-600',
          bg: 'bg-yellow-100 dark:bg-yellow-900',
          ring: 'ring-yellow-200/50',
          chartColor: '#ffbc42',
        };
      case 'error':
        return {
          progress: 'bg-red-50 dark:bg-red-950',
          text: 'text-red-600',
          bg: 'bg-red-100 dark:bg-red-900',
          ring: 'ring-red-200/50',
          chartColor: '#d64045',
        };
      case 'info':
        return {
          progress: 'bg-blue-50 dark:bg-blue-950',
          text: 'text-blue-600',
          bg: 'bg-blue-100 dark:bg-blue-900',
          ring: 'ring-blue-200/50',
          chartColor: '#55BEDB',
        };
      case 'default':
      default:
        return {
          progress: 'bg-gray-50 dark:bg-gray-800',
          text: 'text-gray-600 dark:text-gray-400',
          bg: 'bg-gray-100',
          ring: 'ring-gray-200/50 dark:ring-gray-700/50',
          chartColor: '#8490A0',
        };
    }
  };

  const styles = getVariantStyles();
  const clampedProgress = Math.min(Math.max(progress, 0), 100);

  const renderMiniChart = () => {
    if (!chartData || chartData.length === 0) {
      return null;
    }

    // Convert chartData to design system format
    const sparkChartData = chartData.map((value, index) => ({
      category: index.toString(),
      value,
    }));

    return (
      <div className="mt-4 h-16 w-full">
        <SparkChart
          data={sparkChartData as Record<string, string | number>[]}
          series={['value']}
          type="area"
          categoryKey="category"
          className="h-full w-full"
          showTooltip
          yAxisProps={{
            domain: ['+min', '+max'],
          }}
          colors={[styles.chartColor]}
        />
      </div>
    );
  };

  const renderLoadingContent = () => (
    <div className="animate-pulse">
      <div className="mb-4 flex items-center gap-3">
        <div className="h-5 w-5 rounded bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
      </div>
      <div className="space-y-3">
        <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-4 w-1/2 rounded bg-gray-200 dark:bg-gray-700"></div>
      </div>
    </div>
  );

  return (
    <Card
      className={className}
      onClick={onClick}
      isLoading={isLoading}
      loadingContent={renderLoadingContent()}
    >
      <div
        className={cn(
          'flex flex-col',
          className?.includes('h-full') && 'h-full',
        )}
      >
        <CardHeader>
          <div className="flex items-start gap-3">
            {icon && (
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center text-gray-400 dark:text-gray-500">
                {renderIcon({ name: icon, size: 16 })}
              </div>
            )}

            <div className="min-w-0 flex-1">
              <div className="mb-3 flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                    {titleIcon && (
                      <span className="text-gray-500 dark:text-gray-400">
                        {renderIcon({ name: titleIcon, size: 16 })}
                      </span>
                    )}
                    {title || t('progress')}
                  </h3>
                  {subtitle && (
                    <p className="mt-1 text-xs leading-relaxed font-normal text-gray-600 dark:text-gray-400">
                      {subtitle}
                    </p>
                  )}
                </div>

                {showPercentage && (
                  <div className="ml-4 flex-shrink-0">
                    <span
                      className={twMerge(
                        'text-sm font-semibold tracking-tight',
                        styles.text,
                      )}
                    >
                      {Math.round(clampedProgress)}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent
          className={cn(
            className?.includes('h-full') &&
              'flex flex-1 flex-col justify-center',
          )}
        >
          {(current !== undefined || target !== undefined) && (
            <div className="mb-3 flex items-center gap-2 text-sm font-medium tracking-wide text-gray-600 dark:text-gray-400">
              {current !== undefined && (
                <span className="font-semibold">
                  {current.toLocaleString()}
                  {unit && ` ${unit}`}
                </span>
              )}
              {target !== undefined && (
                <>
                  <span className="font-normal text-gray-400 dark:text-gray-500">
                    /
                  </span>
                  <span className="font-normal">
                    {target.toLocaleString()}
                    {unit && ` ${unit}`}
                  </span>
                </>
              )}
            </div>
          )}

          <div className="relative mb-3">
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className={twMerge(
                  'h-full rounded-full transition-all duration-500 ease-out',
                  styles.progress,
                )}
                style={{ width: `${clampedProgress}%` }}
              />
            </div>
          </div>
        </CardContent>

        <CardFooter className={cn(className?.includes('h-full') && 'mt-auto')}>
          {description && (
            <p className="text-xs leading-relaxed font-normal text-gray-500 dark:text-gray-400">
              {description}
            </p>
          )}
          {renderMiniChart()}
        </CardFooter>
      </div>
    </Card>
  );
};
